import { firebaseInit } from '~/composables/useFirebase'
import type {
  FirebaseUser,
  Auth0User,
  Auth0Role,
  IAward,
  ActionType,
  UserPreferences,
  FirebaseUserActions,
  Hints,
} from '~/types/UserData'
import { zUser, zActions, zPreferences, zHints } from '~/types/UserData'
import { differenceInWeeks } from 'date-fns'
import { isSameWeek } from 'date-fns'
import type { IParticularsEmail } from '~/server/api/send-particulars-mail.post'

export const useUserStore = defineStore('UserStore', {
  state: () => ({
    user: {} as FirebaseUser,
    userRole: { name: 'User', id: 'no-role', description: 'no-role' } as Auth0Role,
    resetLink: '',
    award: {} as IAward,
    decryptedSsn: '',
  }),
  actions: {
    async setUser(id: string) {
      const fb = firebaseInit()
      const firebaseUser: FirebaseUser = await fb.getOneUsersData(id)

      const queryParams = new URLSearchParams({ id })

      // Sync data from Auth0
      const { user: auth0User, role }: { user: Auth0User; role: Auth0Role[] } = await $fetch(
        `/api/auth-user?${queryParams}`,
      )
      // Depends on there only being one role assigned to the user
      this.userRole = role[0] || { name: 'User', id: 'no-role', description: 'no-role' }

      const isSocial = auth0User?.identities?.[0]?.isSocial || firebaseUser?.isSocial || false
      const email = firebaseUser?.email || auth0User?.email || ''
      const name = firebaseUser?.name || auth0User?.name || ''
      const givenName = firebaseUser?.givenName || auth0User?.given_name || ''
      const familyName = firebaseUser?.familyName || auth0User?.family_name || ''
      const country = firebaseUser?.country || auth0User?.user_metadata?.country || 'Sverige'
      const emailNotify = firebaseUser?.emailNotify || auth0User?.user_metadata?.email_notify

      const user = zUser.parse({
        ...firebaseUser,
        isSocial,
        email,
        name,
        givenName,
        familyName,
        country,
        emailNotify,
        // always init correct AuthTokenId
        id,
      })

      // Sync data from CRM -- create/update contact and store partId & hashId
      if (!user.partId && user.givenName && user.familyName && user.email && user.birthDate) {
        const contactRes = await $fetch('/api/contact', {
          method: 'PATCH',
          body: {
            first: user.givenName,
            last: user.familyName,
            email: user.email,
            birthdate: user.birthDate,
          },
        })
        if (contactRes.success && contactRes.partId) {
          user.partId = contactRes.partId
          user.hashId = contactRes.hashId
          // TODO: Add membership check
        }
      }

      // Decrypt SSN if present
      // Fix: If the ssn is decrypted the decrypt value gets stored in the db again

      if (user.ssn && user.ssn.length > 12) {
        const { decrypted } = await $fetch<{ decrypted: string }>('/api/crypto/decrypt', {
          method: 'POST',
          body: { encryptedText: user.ssn },
        })
        this.decryptedSsn = decrypted || ''
      }

      this.user = zUser.parse(user)
      await fb.updateUserDocument(id, zUser.parse(this.user))

      // Set award
      await useAwards(user.actions)
    },
    async setEntraUser(token: { sub: string; name: string; email: string }) {
      const fb = firebaseInit()
      const firebaseUser: FirebaseUser = await fb.getOneUsersData(token.sub)

      const hasExistingData = firebaseUser?.id && firebaseUser.id !== 'init'

      this.user = zUser.parse({
        ...(hasExistingData ? firebaseUser : {}),
        id: token.sub,
        name: token.name || firebaseUser?.name || '',
        email: token.email || firebaseUser?.email || '',
        givenName: token.name?.split(' ')[0] || firebaseUser?.givenName || '',
        familyName: token.name?.split(' ').slice(1).join(' ') || firebaseUser?.familyName || '',
        userAccess: 'admin',
      })

      await fb.updateUserDocument(token.sub, zUser.parse(this.user))
      this.userRole = { name: 'Admin', id: 'entra-admin', description: 'Entra ID admin' } as Auth0Role
    },
    async setPreference(key: keyof UserPreferences) {
      if (this.user.preferences) {
        this.user.preferences[key] = !this.user.preferences[key]
      }
    },
    async updatePreferences() {
      const id = this.user.id
      if (!id) {
        return
      }
      const preferences = this.user.preferences
      const fb = firebaseInit()
      await fb.updateUserPreferences(id, preferences as UserPreferences)
      useNuxtApp().$toast.success('Då har vi uppdaterat det!', {
        position: 'top',
        dismissible: true,
      })
    },
    async setUserActions(actions: FirebaseUserActions) {
      const id = this.user.id
      if (!id) {
        return
      }
      const fb = firebaseInit()
      fb.updateUserActions(id, actions)
    },
    async updateScore({
      score,
      id,
      type,
      title,
    }: {
      score: number
      id: string
      type: ActionType
      title: string
    }) {
      const actions = zActions.parse(this.user.actions)
      const today = new Date()

      // Mark action as done
      actions.done.push({
        id,
        doneDate: today.toISOString(),
        title: title || '',
        type: type || '',
      })

      // Update score
      actions.score = actions.score ? actions.score + score : 0 + score

      // Handle week streak
      const lastTimeActivityDone =
        actions.weekScore.timeStamp === 0 ? today.getTime() : actions.weekScore.timeStamp
      const missedWeeks = differenceInWeeks(today, new Date(lastTimeActivityDone as number))
      const isTheSameWeek = isSameWeek(today, new Date(lastTimeActivityDone as number))

      // Preserve longest streak before any reset
      actions.weekScore.longest = Math.max(actions.weekScore.longest || 0, actions.weekScore.score)

      // Reset week streak if the user has missed one or more weeks of activity
      if (missedWeeks >= 2) {
        actions.weekScore.score = 0
      } else if (missedWeeks === 1 && !isTheSameWeek) {
        // Add to week streak if the user is active several weeks in a row
        actions.weekScore.score++
      } else if (missedWeeks === 0 && isTheSameWeek && actions.weekScore.score === 0) {
        actions.weekScore.score = 1
      }

      // Update longest streak
      if (!actions.weekScore.longest || actions.weekScore.score > actions.weekScore.longest) {
        actions.weekScore.longest = actions.weekScore.score
      }

      // Touch timestamp
      actions.weekScore.timeStamp = today.getTime()

      this.user.actions = actions
      this.setUserActions(actions)
      return actions
    },
    async closeHint({ type }: { type: keyof Hints }) {
      const id = this.user.id
      if (!id) {
        return
      }
      const fb = firebaseInit()

      const showHints = zHints.parse(this.user.hints ?? {})

      const updatedHints = {
        ...showHints,
        [type]: false,
      }
      this.user.hints = updatedHints

      await fb.updateUserHints(id, updatedHints)
    },
    async initUser() {
      const id = this.user.id
      if (!id) {
        return
      }
      const data = {
        ...this.user,
        preferences: this.user.preferences,
        id,
        emailNotify: true,
      }
      const fb = firebaseInit()
      await fb.initUserDocument(id, zUser.parse(data))
    },
    async updateUser(data: FirebaseUser, message: string = 'Då har vi uppdaterat det!') {
      const id = this.user.id
      if (!id) {
        return
      }

      const shouldUpdateEmailNotify: boolean = this.user.emailNotify !== data.emailNotify
      const fb = firebaseInit()
      const user = { ...this.user, ...(data || {}) }

      this.user = zUser.parse(user)

      await fb.updateUserDocument(id, data)

      if (shouldUpdateEmailNotify) {
        await this.updateEmailNotify(data.emailNotify ?? false)
      }

      if (message) {
        useNuxtApp().$toast.success(message, {
          position: 'top',
          dismissible: true,
        })
      }
    },
    async sendEmails(preferences: UserPreferences) {
      if (!preferences) {
        return
      }
      const values = Object.keys(preferences).filter(
        key => preferences[key as keyof UserPreferences],
      )
      const data = { preferenceTypes: values }
      try {
        const response = await $fetch('/api/send-mail-all', {
          method: 'POST',
          body: data,
        })
        console.log('response', response)
        useNuxtApp().$toast.success('Mejl skickade', {
          position: 'top',
          dismissible: true,
        })
        return response
      } catch (error) {
        console.error(error)
        useNuxtApp().$toast.error('Något gick fel när vi skulle skicka mejlen', {
          position: 'top',
          dismissible: true,
        })
        return error
      }
    },
    async sendParticulars(selectedActions: IParticularsEmail[]) {
      if (!selectedActions) {
        return
      }

      const data = { selectedActions }
      try {
        const response = await $fetch('/api/send-particulars-mail', {
          method: 'POST',
          body: data,
        })
        console.log('response', response)
        useNuxtApp().$toast.success('Mejl skickade', {
          position: 'top',
          dismissible: true,
        })
        return response
      } catch (error) {
        console.error(error)
        useNuxtApp().$toast.error('Något gick fel när vi skulle skicka mejlen', {
          position: 'top',
          dismissible: true,
        })
        return error
      }
    },
    async generateResetLPasswordLink() {
      const obj = {
        // TODO: Check if this url is correct -- that the reset functionality works
        result_url: 'https://blixtapp.djurensratt.se/',
        user_id: this.user.id,
      }
      try {
        const data = await $fetch('/api/auth-user/reset-password', {
          method: 'POST',
          body: JSON.stringify(obj),
        })

        this.resetLink = data
      } catch (error) {
        console.error(error)
      }
    },
    async setAward(award: IAward) {
      this.award = award
    },
    async updateEmailNotify(emailNotify: boolean) {
      const id = this.user.id
      if (!id) {
        return
      }
      const res = await $fetch('/api/auth-user', {
        method: 'POST',
        body: {
          id,
          data: {
            user_metadata: {
              email_notify: emailNotify,
            },
          },
        },
      })

      return res
    },
  },
  getters: {
    getUserRole: state => state.userRole,
    getUser: state => state.user,
    getUserPreferences: state => state.user?.preferences ?? {},
    getUserDoneActions: state => state?.user?.actions?.done || [],
    getUserHiddenActions: state => state?.user?.actions?.hidden || [],
    getUserHints: state => state.user?.hints ?? {},
    getResetLink: state => state.resetLink,
    getUserActions: state => state.user.actions,
    getAward: state => state.award,
    getUpdates: state => state.user?.campaignUpdates || [],
  },
})
